import ENDPOINTS from '@/config/apiEndpoints';
import APP_CONFIG from '@/config/app';
import { normalizeWalletsResponse } from '@/services/normalizeWalletsResponse';
import reactQueryClient from '@/services/ReactQueryClientService';
import { logDebug, logError, logInfo } from '@/utils/logger';

const QUERY_KEY_WALLETS = ['wallets'] as const;
const BETA_RESTRICTION_ERROR_CODE = 'UNAUTHORIZED';
const BETA_RESTRICTION_ERROR_MESSAGE = 'Not part of beta program';

const buildApiUrl = (baseUrl: string, endpoint: string): string => {
  let url =
    baseUrl.endsWith('/') && endpoint.startsWith('/')
      ? `${baseUrl}${endpoint.substring(1)}`
      : `${baseUrl}${endpoint}`;

  if (url.endsWith('/')) {
    url = url.slice(0, -1);
  }

  return url;
};

type AuthorizationProbeError = Error & {
  status?: number;
  errorData?: any;
  isBetaRestricted?: boolean;
};

type AuthStatusCallback = (isAuthorized: boolean) => void;
class AuthorizationServiceClass {
  private _isAuthorized = true;
  private _subscribers: Set<AuthStatusCallback> = new Set();
  private _authorizationCheckPromise: Promise<boolean> | null = null;
  public getAuthorizationStatus(): boolean {
    logDebug(
      'Getting authorization status',
      'AuthorizationService.getAuthorizationStatus',
      {
        isAuthorized: this._isAuthorized,
      },
    );
    return this._isAuthorized;
  }
  public setUnauthorized(): void {
    if (!this._isAuthorized) {
      return;
    }
    logInfo(
      'Setting user as unauthorized for beta access',
      'AuthorizationService.setUnauthorized',
    );
    this._isAuthorized = false;
    this._notifySubscribers();
  }
  public setAuthorized(): void {
    if (this._isAuthorized) {
      return;
    }
    logInfo(
      'Setting user as authorized for beta access',
      'AuthorizationService.setAuthorized',
    );
    this._isAuthorized = true;
    this._notifySubscribers();
  }
  public async checkAuthorizationStatus(): Promise<boolean> {
    const caller = 'AuthorizationService.checkAuthorizationStatus';
    if (this._authorizationCheckPromise) {
      logInfo(
        'Authorization check already in progress. Reusing in-flight request.',
        caller,
      );
      return this._authorizationCheckPromise;
    }

    const promise = this._checkAuthorizationStatusInternal();
    this._authorizationCheckPromise = promise;

    try {
      return await promise;
    } finally {
      this._authorizationCheckPromise = null;
    }
  }
  private async _checkAuthorizationStatusInternal(): Promise<boolean> {
    const caller = 'AuthorizationService.checkAuthorizationStatus';
    logInfo('Checking authorization status', caller);
    try {
      const authToken = sessionStorage.getItem('authToken');
      if (!authToken) {
        logInfo('No auth token found, user is not authenticated', caller);
        return false;
      }

      const url = buildApiUrl(
        APP_CONFIG.API_SERVICE.BASE_URL || '',
        ENDPOINTS.USER_WALLETS,
      );

      await reactQueryClient.fetchQuery({
        queryKey: QUERY_KEY_WALLETS,
        staleTime: 0,
        retry: false,
        queryFn: async () => {
          logInfo(`Making authorization probe request to ${url}`, caller);
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });

          logInfo(`Received response with status ${response.status}`, caller);
          if (response.ok) {
            const responseData = await response.json();
            if (!Array.isArray(responseData)) {
              throw new Error('Wallets authorization probe returned non-array data');
            }

            return normalizeWalletsResponse(responseData);
          }

          let errorData: any = null;
          try {
            errorData = await response.json();
          } catch (parseError) {
            logError(parseError, `${caller}.parseError`, {
              message: 'Error parsing non-200 authorization probe response',
              status: response.status,
            });
          }

          const error: AuthorizationProbeError = new Error(
            `Authorization probe failed with status ${response.status}`,
          );
          error.status = response.status;
          error.errorData = errorData;
          error.isBetaRestricted =
            response.status === 401 &&
            errorData?.errorCode === BETA_RESTRICTION_ERROR_CODE &&
            errorData?.errorMessage === BETA_RESTRICTION_ERROR_MESSAGE;

          throw error;
        },
      });

      if (!this._isAuthorized) {
        logInfo(
          'Updating authorization status from unauthorized to authorized',
          caller,
        );
        this.setAuthorized();
        return true;
      }
      return false;
    } catch (error: any) {
      if (error?.isBetaRestricted) {
        reactQueryClient.removeQueries({ queryKey: QUERY_KEY_WALLETS });
        if (this._isAuthorized) {
          logInfo(
            'Updating authorization status from authorized to unauthorized',
            caller,
          );
          this.setUnauthorized();
          return true;
        }
        return false;
      }

      logError(error, caller, {
        message: 'Error checking authorization status',
      });
      return false;
    }
  }
  public subscribe(callback: AuthStatusCallback): () => void {
    this._subscribers.add(callback);
    callback(this._isAuthorized);
    return () => this.unsubscribe(callback);
  }
  public unsubscribe(callback: AuthStatusCallback): void {
    this._subscribers.delete(callback);
  }
  private _notifySubscribers(): void {
    this._subscribers.forEach((callback) => {
      try {
        callback(this._isAuthorized);
      } catch (error) {
        logError(error, 'AuthorizationService._notifySubscribers', {
          subscriberCount: this._subscribers.size,
        });
      }
    });
  }
}
const AuthorizationService = new AuthorizationServiceClass();
export default AuthorizationService;
